// Slide 3 - Mazville
import { ExternalLink, Award, Zap, Users } from "lucide-react";

export default function Slide3() {
  const images = [
    "/mazville/mazville_1.png",
    "/mazville/mazville_2.png",
    "/mazville/mazville_3.png",
    "/mazville/mazville_4.png",
    "/mazville/mazville_5.png",
  ];

  return (
    <section className="w-full max-w-[1400px] relative z-10">
      {/* Decorative accent blob */}
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none opacity-60 hidden md:block" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-10 items-start">
        {/* Left: Content */}
        <div className="space-y-2 sm:space-y-3 md:space-y-5 p-4 sm:p-6 md:p-10 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-white/8 via-white/4 to-transparent backdrop-blur-xl border border-white/20 shadow-2xl relative overflow-hidden group hover:border-white/30 transition-all duration-500">
          {/* Shimmer effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-gradient-to-r from-white/15 to-white/5 rounded-full text-white text-[9px] sm:text-[10px] md:text-xs uppercase tracking-wider font-bold border border-white/30 shadow-lg">
                <Zap size={10} className="opacity-80 sm:w-3 sm:h-3 md:w-4 md:h-4" />
                Website
              </span>
            </div>

            <div className="mt-2 sm:mt-4 md:mt-6">
              <h2
                className="text-white font-black leading-none tracking-tight drop-shadow-lg"
                style={{
                  fontSize: "clamp(28px, 8vw, 58px)",
                  textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                Mazville
              </h2>
              <div className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-white/10 rounded-lg mt-1 sm:mt-2">
                <span className="text-white/70 uppercase tracking-widest text-[9px] sm:text-[10px] md:text-xs font-bold">
                  Hospitality Website
                </span>
              </div>
            </div>

            <p className="text-white/90 text-xs sm:text-sm md:text-base leading-5 sm:leading-6 md:leading-7 mt-2 sm:mt-4 md:mt-6 max-w-[580px]">
              A refined, <span className="keyword">SEO-optimized</span> digital
              experience for boutique hospitality brands. Designed with a
              <span className="keyword"> strong visual hierarchy</span>,
              <span className="keyword"> polished micro-interactions</span>, and
              <span className="keyword"> bespoke watercolor illustrations</span>
              , the platform elegantly highlights rooms, amenities, and curated
              local experiences.
              <br />
              <br className="hidden sm:block" />
              Delivered as a full{" "}
              <span className="keyword">end-to-end solution</span>, the website
              seamlessly integrates{" "}
              <span className="keyword">booking functionality</span>
              within a thoughtfully crafted,
              <span className="keyword"> high-conversion user journey</span>.
            </p>

            <div className="h-px bg-white/6 my-2 sm:my-4 md:my-6" />

            {/* Features - compact single-line buttons */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3">
              <button className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md bg-white/6 border border-white/8 text-[10px] sm:text-xs md:text-sm font-semibold text-white shadow-sm hover:shadow-md transition transform hover:-translate-y-0.5">
                <span className="p-0.5 sm:p-1 bg-white/10 rounded-sm flex items-center justify-center">
                  <Award size={10} className="text-white opacity-90 sm:w-3 sm:h-3 md:w-4 md:h-4" />
                </span>
                Design system
              </button>

              <button className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md bg-white/6 border border-white/8 text-[9px] sm:text-[10px] md:text-sm font-semibold text-white shadow-sm hover:shadow-md transition transform hover:-translate-y-0.5">
                <span className="p-0.5 sm:p-1 bg-white/10 rounded-sm flex items-center justify-center">
                  <Zap size={10} className="text-white opacity-90 sm:w-3 sm:h-3 md:w-4 md:h-4" />
                </span>
                SEO & performance
              </button>

              <button className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-md bg-white/6 border border-white/8 text-[9px] sm:text-[10px] md:text-sm font-semibold text-white shadow-sm hover:shadow-md transition transform hover:-translate-y-0.5">
                <span className="p-0.5 sm:p-1 bg-white/10 rounded-sm flex items-center justify-center">
                  <Users size={10} className="text-white opacity-90 sm:w-3 sm:h-3 md:w-4 md:h-4" />
                </span>
                Responsive
              </button>
            </div>

            <div className="mt-2 sm:mt-4 md:mt-6 flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap">
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
                <div className="text-white/70 text-[10px] sm:text-xs md:text-sm font-semibold">
                  Delivered in
                </div>
                <span className="relative flex items-center">
                  <span className="absolute inline-flex h-2 w-2 sm:h-3 sm:w-3 md:h-3.5 md:w-3.5 rounded-full bg-white/60 opacity-75 animate-ping" />
                  <span className="relative inline-block h-1.5 w-1.5 sm:h-2 sm:w-2 md:h-2.5 md:w-2.5 rounded-full bg-white shadow" />
                </span>
                <div className="text-white font-medium text-xs sm:text-sm md:text-sm">27 days</div>
              </div>

              <div className="ml-auto">
                <a
                  href="https://mazville.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 bg-white text-gray-900 px-2 sm:px-4 md:px-6 py-1 sm:py-1.5 md:py-2.5 rounded-full font-semibold shadow-md transition-transform hover:scale-105 border border-white/20 text-[10px] sm:text-xs md:text-sm"
                >
                  Visit website
                  <ExternalLink size={12} className="sm:w-3 sm:h-3 md:w-4 md:h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Enhanced Gallery */}
        <div className="relative">
          {/* Decorative glow */}
          <div className="absolute -inset-4 bg-gradient-to-br from-white/10 via-transparent to-white/5 rounded-3xl blur-2xl opacity-40 pointer-events-none hidden md:block" />

          <div className="relative grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-2.5">
            {/* Hero image with overlay effect */}
            <div className="col-span-2 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border border-white/20 sm:border-2 group/img relative">
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 z-10" />
              <img
                src={images[0]}
                alt="Mazville hero"
                className="w-full h-32 sm:h-48 md:h-64 lg:h-[360px] object-cover transition-transform duration-700"
              />
            </div>

            {/* Thumbnail grid with hover effects */}
            {images.slice(1).map((img, idx) => (
              <div
                key={idx}
                className="rounded-lg sm:rounded-xl overflow-hidden shadow-xl border border-white/15 group/thumb relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/20 opacity-0 transition-opacity duration-300 z-10" />
                <img
                  src={img}
                  alt={`Mazville detail ${idx + 1}`}
                  className="w-full h-16 sm:h-24 md:h-32 lg:h-36 object-cover transition-all duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
